import { apiClient } from './client';

const base = '/api/v1/finance/fixed-assets';

export type DepreciationMethod = 'SLM' | 'WDV' | 'NONE';
export type CapitalizationMode = 'BANK' | 'CASH' | 'VENDOR_CREDIT' | 'PURCHASE_INVOICE' | 'OPENING';
export type GstType = 'NONE' | 'CGST_SGST' | 'IGST';
export type AssetStatus = 'ACTIVE' | 'UNDER_MAINTENANCE' | 'FULLY_DEPRECIATED' | 'DISPOSED' | 'SCRAPPED' | 'WRITTEN_OFF';
export type AssetCondition = 'GOOD' | 'FAIR' | 'POOR' | 'DAMAGED' | 'MISSING';
export type DisposalType = 'SALE' | 'SCRAP' | 'WRITE_OFF';

export interface AssetCategory {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  it_depreciation_rate: number;
  book_depreciation_rate: number;
  depreciation_method: DepreciationMethod;
  useful_life_years: number | null;
  coa_asset_account_id?: string | null;
  coa_depreciation_account_id?: string | null;
  coa_expense_account_id?: string | null;
  asset_acc_code?: string | null;
  asset_acc_name?: string | null;
  depr_acc_code?: string | null;
  depr_acc_name?: string | null;
  exp_acc_code?: string | null;
  exp_acc_name?: string | null;
  asset_count: number;
  is_active: boolean;
}

export interface FixedAsset {
  id: string;
  asset_code: string;
  asset_name: string;
  description?: string | null;
  category_id: string;
  category_name: string;
  category_code: string;
  sub_category?: string | null;
  serial_number?: string | null;
  make_model?: string | null;
  purchase_date: string;
  depreciation_start_date: string;
  vendor_id?: string | null;
  vendor_name?: string | null;
  purchase_invoice_id?: string | null;
  purchase_invoice_number?: string | null;
  capitalization_mode: CapitalizationMode;
  payment_account_id?: string | null;
  payment_account_name?: string | null;
  purchase_cost: number;
  additional_cost: number;
  total_capitalized_cost: number;
  capital_improvements: number;
  scrap_value: number;
  gst_type: GstType;
  cgst_amount: number;
  sgst_amount: number;
  igst_amount: number;
  gst_paid: number;
  itc_eligible: boolean;
  warehouse_id?: string | null;
  warehouse_name?: string | null;
  department?: string | null;
  location_detail?: string | null;
  custodian_id?: string | null;
  custodian_name?: string | null;
  depreciation_method: DepreciationMethod;
  depreciation_rate: number;
  useful_life_years?: number | null;
  current_book_value: number;
  accumulated_depreciation: number;
  opening_accumulated_depreciation: number;
  depreciated_upto?: string | null;
  next_depreciation_date?: string | null;
  last_verified_date?: string | null;
  condition: AssetCondition;
  insurance_policy_no?: string | null;
  insurance_company?: string | null;
  insurance_expiry?: string | null;
  insured_value?: number | null;
  warranty_expiry?: string | null;
  machine_id?: string | null;
  machine_name?: string | null;
  capex_request_id?: string | null;
  capex_request_number?: string | null;
  status: AssetStatus;
  disposal_date?: string | null;
  disposal_type?: DisposalType | null;
  disposal_amount?: number | null;
  disposal_buyer?: string | null;
  disposal_reason?: string | null;
  disposal_depreciation?: number;
  profit_loss_on_disposal?: number | null;
  created_at: string;
}

export interface AssetSummary {
  activeCount: number;
  underMaintenanceCount: number;
  fullyDepreciatedCount: number;
  disposedCount: number;
  totalCount: number;
  grossBlock: number;
  netBlock: number;
  totalDepreciation: number;
  dueForVerification: number;
  insuranceExpiringSoon: number;
  depreciationPending: number;
  capexPending: number;
  capexApproved: number;
}

export interface ProjectedScheduleItem {
  yearNumber: number;
  financialYear: string;
  openingBookValue: number;
  depreciationAmount: number;
  closingBookValue: number;
}

export interface AssetMovement {
  id: string;
  movement_type: string;
  from_location?: string | null;
  to_location?: string | null;
  movement_date: string;
  amount?: number | null;
  remarks?: string | null;
  performed_by_email?: string | null;
}

export interface AssetMaintenance {
  id: string;
  maintenance_date: string;
  maintenance_type: 'REVENUE' | 'CAPITAL';
  work_category?: string | null;
  description: string;
  vendor_name?: string | null;
  amount: number;
  gst_amount: number;
  payment_mode: 'BANK' | 'CASH' | 'VENDOR_CREDIT';
  payment_account_name?: string | null;
  life_extension_years: number;
  downtime_days: number;
  reference_no?: string | null;
  voucher_number?: string | null;
}

export interface AssetDetailResponse {
  asset: FixedAsset;
  movements: AssetMovement[];
  depreciationHistory: {
    id: string;
    period_month: number;
    period_year: number;
    period_from?: string | null;
    period_end?: string | null;
    financial_year: string;
    opening_book_value: number;
    depreciation_amount: number;
    closing_book_value: number;
    voucher_number?: string | null;
  }[];
  maintenance: AssetMaintenance[];
  machineWorkOrders: {
    mwo_number: string;
    task_name: string;
    schedule_type?: string | null;
    scheduled_date?: string | null;
    status: string;
    completed_at?: string | null;
    findings?: string | null;
  }[];
  vouchers: { id: string; voucher_number: string; voucher_type: string; voucher_date: string; total_debit: number; narration: string }[];
  projectedSchedule: ProjectedScheduleItem[];
}

export interface AssetInput {
  assetName: string;
  assetCode?: string;
  categoryId: string;
  description?: string;
  subCategory?: string;
  serialNumber?: string;
  makeModel?: string;
  purchaseDate: string;
  depreciationStartDate?: string;
  capitalizationMode: CapitalizationMode;
  paymentAccountId?: string;
  vendorId?: string;
  purchaseInvoiceId?: string;
  purchaseCost: number;
  additionalCost?: number;
  gstType?: GstType;
  gstAmount?: number;
  itcEligible?: boolean;
  scrapValue?: number;
  depreciationMethod?: DepreciationMethod;
  depreciationRate?: number;
  usefulLifeYears?: number | null;
  openingAccumulatedDepreciation?: number;
  openingDepreciationUpto?: string;
  warehouseId?: string;
  department?: string;
  locationDetail?: string;
  custodianName?: string;
  insurancePolicyNo?: string;
  insuranceCompany?: string;
  insuranceExpiry?: string;
  insuredValue?: number;
  warrantyExpiry?: string;
  machineId?: string | null;
  capexRequestId?: string;
}

export interface DepreciationPreviewLine {
  assetId: string;
  assetCode: string;
  assetName: string;
  categoryName: string;
  method: DepreciationMethod;
  depreciationRate: number;
  usefulLifeYears?: number | null;
  periodFrom: string;
  periodTo: string;
  isCatchUp: boolean;
  openingBookValue: number;
  depreciationAmount: number;
  closingBookValue: number;
  scrapValue: number;
}

export interface DepreciationPreviewResponse {
  alreadyRun: boolean;
  periodMonth: number;
  periodYear: number;
  periodStart: string;
  periodEnd: string;
  financialYear: string;
  totalAssets: number;
  totalDepreciation: number;
  lines: DepreciationPreviewLine[];
  categorySummaries: { categoryName: string; totalAmount: number; expenseAccName: string; deprAccName: string }[];
}

export interface DepreciationRunHistoryItem {
  id: string;
  run_date: string;
  financial_year: string;
  period_month: number;
  period_year: number;
  period_start?: string | null;
  period_end?: string | null;
  total_assets_processed: number;
  total_depreciation_amount: number;
  jv_number?: string | null;
  jv_id?: string | null;
  run_by_email?: string | null;
  created_at: string;
}

export interface DisposalPreview {
  type: DisposalType;
  date: string;
  pendingDepreciation: number;
  bookValueBefore: number;
  netBookValue: number;
  proceeds: number;
  gst: { type: GstType; cgst: number; sgst: number; igst: number; total: number };
  accumulatedDepreciation: number;
  cost: number;
  profitLoss: number;
}

export interface DisposalInput {
  disposalType: DisposalType;
  disposalDate: string;
  disposalAmount?: number;
  gstType?: GstType;
  gstAmount?: number;
  bankAccountId?: string;
  disposalBuyer?: string;
  disposalReason?: string;
}

export interface MaintenanceInput {
  maintenanceType: 'REVENUE' | 'CAPITAL';
  maintenanceDate: string;
  workCategory?: string;
  description: string;
  amount: number;
  gstType?: GstType;
  gstAmount?: number;
  itcEligible?: boolean;
  paymentMode: 'BANK' | 'CASH' | 'VENDOR_CREDIT';
  paymentAccountId?: string;
  vendorId?: string;
  lifeExtensionYears?: number;
  downtimeDays?: number;
  referenceNo?: string;
  markUnderMaintenance?: boolean;
}

export interface CapexRequest {
  id: string;
  request_number: string;
  asset_name: string;
  category_id?: string | null;
  category_name?: string | null;
  estimated_cost: number;
  justification?: string | null;
  expected_life_years?: number | null;
  department?: string | null;
  required_by?: string | null;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CAPITALIZED' | 'CANCELLED';
  requested_by_email?: string | null;
  decided_by_email?: string | null;
  decided_at?: string | null;
  decision_remarks?: string | null;
  asset_id?: string | null;
  asset_code?: string | null;
  created_at: string;
}

export interface VerificationItem {
  id: string;
  asset_id: string;
  asset_code: string;
  asset_name: string;
  serial_number?: string | null;
  category_name: string;
  expected_location?: string | null;
  actual_location?: string | null;
  condition: AssetCondition;
  remarks?: string | null;
  status: 'PENDING' | 'VERIFIED' | 'DISCREPANCY' | 'MISSING';
  verified_at?: string | null;
}

export interface VerificationSession {
  id: string;
  verification_code: string;
  verification_date: string;
  financial_year: string;
  status: 'IN_PROGRESS' | 'COMPLETED';
  total_assets: number;
  verified_count: number;
  missing_count: number;
  damaged_count: number;
  remarks?: string | null;
  verified_by_email?: string | null;
  created_at: string;
}

export interface ScheduleRow {
  categoryId: string;
  categoryName: string;
  assets: number;
  grossOpening: number;
  additions: number;
  deletions: number;
  grossClosing: number;
  depOpening: number;
  depAcquired: number;
  depForYear: number;
  depOnDeletions: number;
  depClosing: number;
  nbvOpening: number;
  nbvClosing: number;
}

export interface FixedAssetReports {
  financialYear: string;
  period: { start: string; end: string };
  schedule: ScheduleRow[];
  scheduleTotals: Omit<ScheduleRow, 'categoryId' | 'categoryName' | 'assets'>;
  taxBlocks: {
    categoryId: string;
    categoryName: string;
    rate: number;
    openingWdv: number;
    additionsOver180Days: number;
    additionsUnder180Days: number;
    deletions: number;
    depreciation: number;
    closingWdv: number;
  }[];
  movements: {
    additions: { assetId: string; assetCode: string; assetName: string; categoryName: string; date: string; cost: number; mode: CapitalizationMode; vendorName?: string | null }[];
    improvements: { assetId: string; assetCode: string; assetName: string; date: string; value: number; description: string }[];
    disposals: { assetId: string; assetCode: string; assetName: string; categoryName: string; date: string; type: DisposalType; cost: number; accumulatedDepreciation: number; proceeds: number; profitLoss: number }[];
  };
  fullyDepreciated: { assetId: string; assetCode: string; assetName: string; categoryName: string; cost: number; bookValue: number; location: string }[];
  locations: { warehouse: string; department: string; location: string; count: number; cost: number; bookValue: number; assets: { assetCode: string; assetName: string; custodian: string | null }[] }[];
  forecast: { month: string; depreciation: number }[];
}

type Res<T> = { success: boolean; data: T; message?: string };

export const fixedAssetsApi = {
  // Categories
  getCategories: async (includeInactive = false) =>
    (await apiClient.get<Res<AssetCategory[]>>(`${base}/categories`, { params: { includeInactive } })).data.data,
  createCategory: async (data: Record<string, unknown>) => (await apiClient.post<Res<AssetCategory>>(`${base}/categories`, data)).data,
  updateCategory: async (id: string, data: Record<string, unknown>) => (await apiClient.put<Res<AssetCategory>>(`${base}/categories/${id}`, data)).data,

  // Register
  getSummary: async () => (await apiClient.get<Res<AssetSummary>>(`${base}/summary`)).data.data,
  getMachines: async () =>
    (await apiClient.get<Res<{ id: string; name: string; model?: string | null; status?: string | null; linked_asset_code?: string | null }[]>>(`${base}/machines`)).data.data,
  getEligibleInvoices: async () =>
    (await apiClient.get<Res<{ id: string; invoice_number: string; supplier_invoice_no?: string | null; invoice_date: string; taxable_amount: number; total_amount: number; supplier_name?: string | null }[]>>(`${base}/eligible-invoices`)).data.data,
  getAssets: async (params?: { categoryId?: string; status?: string; department?: string; search?: string; page?: number; limit?: number }) =>
    (await apiClient.get<Res<{ assets: FixedAsset[]; total: number; page: number; limit: number; totalPages: number }>>(`${base}/assets`, { params })).data.data,
  getAssetById: async (id: string) => (await apiClient.get<Res<AssetDetailResponse>>(`${base}/assets/${id}`)).data.data,
  createAsset: async (data: AssetInput) => (await apiClient.post<Res<FixedAsset>>(`${base}/assets`, data)).data,
  updateAsset: async (id: string, data: Partial<AssetInput> & Record<string, unknown>) => (await apiClient.put<Res<FixedAsset>>(`${base}/assets/${id}`, data)).data,
  deleteAsset: async (id: string) => (await apiClient.delete<Res<{ success: boolean }>>(`${base}/assets/${id}`)).data,
  setStatus: async (id: string, status: 'ACTIVE' | 'UNDER_MAINTENANCE', remarks?: string) =>
    (await apiClient.post<Res<{ success: boolean }>>(`${base}/assets/${id}/status`, { status, remarks })).data,

  // Maintenance
  addMaintenance: async (assetId: string, data: MaintenanceInput) => (await apiClient.post<Res<AssetMaintenance>>(`${base}/assets/${assetId}/maintenance`, data)).data,
  deleteMaintenance: async (maintenanceId: string) => (await apiClient.delete<Res<{ success: boolean }>>(`${base}/maintenance/${maintenanceId}`)).data,

  // Depreciation
  previewDepreciation: async (data: { month?: number; year: number; runType?: 'MONTHLY' | 'ANNUAL' }) =>
    (await apiClient.post<Res<DepreciationPreviewResponse>>(`${base}/depreciation/preview`, data)).data.data,
  executeDepreciation: async (data: { month?: number; year: number; runType?: 'MONTHLY' | 'ANNUAL' }) =>
    (await apiClient.post<Res<{ runId: string; journalVoucherNumber: string; totalAssets: number; totalDepreciation: number }>>(`${base}/depreciation/run`, data)).data,
  reverseDepreciation: async (runId: string) => (await apiClient.post<Res<{ success: boolean }>>(`${base}/depreciation/runs/${runId}/reverse`)).data,
  getDepreciationRuns: async () => (await apiClient.get<Res<DepreciationRunHistoryItem[]>>(`${base}/depreciation/runs`)).data.data,

  // Disposal
  previewDisposal: async (id: string, data: DisposalInput) => (await apiClient.post<Res<DisposalPreview>>(`${base}/assets/${id}/dispose/preview`, data)).data.data,
  disposeAsset: async (id: string, data: DisposalInput) =>
    (await apiClient.post<Res<{ asset: FixedAsset; disposalJvNumber: string; profitLoss: number }>>(`${base}/assets/${id}/dispose`, data)).data,
  cancelDisposal: async (id: string) => (await apiClient.post<Res<{ success: boolean }>>(`${base}/assets/${id}/dispose/cancel`)).data,

  // CAPEX
  getCapexRequests: async () => (await apiClient.get<Res<CapexRequest[]>>(`${base}/capex`)).data.data,
  createCapexRequest: async (data: Record<string, unknown>) => (await apiClient.post<Res<CapexRequest>>(`${base}/capex`, data)).data,
  decideCapexRequest: async (id: string, decision: 'APPROVE' | 'REJECT' | 'CANCEL', remarks?: string) =>
    (await apiClient.post<Res<CapexRequest>>(`${base}/capex/${id}/decision`, { decision, remarks })).data,

  // Physical verification
  getVerifications: async () => (await apiClient.get<Res<VerificationSession[]>>(`${base}/verification`)).data.data,
  startVerification: async (data?: { remarks?: string }) => (await apiClient.post<Res<VerificationSession>>(`${base}/verification/start`, data || {})).data,
  getVerificationSession: async (id: string) =>
    (await apiClient.get<Res<{ session: VerificationSession; items: VerificationItem[] }>>(`${base}/verification/${id}`)).data.data,
  updateVerificationItem: async (verificationId: string, itemId: string, data: { condition: AssetCondition; actualLocation?: string; remarks?: string; status?: string }) =>
    (await apiClient.put<Res<VerificationItem>>(`${base}/verification/${verificationId}/item/${itemId}`, data)).data.data,
  completeVerification: async (id: string) => (await apiClient.post<Res<VerificationSession>>(`${base}/verification/${id}/complete`)).data,

  // Reports
  getReports: async (financialYear?: string) => (await apiClient.get<Res<FixedAssetReports>>(`${base}/reports`, { params: { financialYear } })).data.data,
};

export const STATUS_LABEL: Record<AssetStatus, { label: string; cls: string }> = {
  ACTIVE: { label: 'Active', cls: 'bg-green-50 text-green-700 border-green-200' },
  UNDER_MAINTENANCE: { label: 'Under Maintenance', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  FULLY_DEPRECIATED: { label: 'Fully Depreciated', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
  DISPOSED: { label: 'Sold', cls: 'bg-red-50 text-red-700 border-red-200' },
  SCRAPPED: { label: 'Scrapped', cls: 'bg-red-50 text-red-700 border-red-200' },
  WRITTEN_OFF: { label: 'Written Off', cls: 'bg-red-50 text-red-700 border-red-200' },
};

export const MODE_LABEL: Record<CapitalizationMode, string> = {
  BANK: 'Paid by bank',
  CASH: 'Paid in cash',
  VENDOR_CREDIT: 'On credit from vendor',
  PURCHASE_INVOICE: 'Billed on a purchase invoice',
  OPENING: 'Opening balance (already owned)',
};

export const currentFinancialYear = () => {
  const d = new Date();
  const start = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return `${start}-${String(start + 1).slice(-2)}`;
};
